// Bulk Tasks Dialogs — Import (CSV) + Export (filtered CSV).
// Lives on the Client Workflow page next to "Create Task".

import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Download, Upload, AlertCircle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ BULK IMPORT ============

export function BulkImportTasksDialog({ open, onOpenChange, clientId, onImported }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // { validCount, errorCount, errors, preview }
  const [step, setStep] = useState('pick');     // pick → preview → done

  const reset = () => {
    setFile(null); setPreview(null); setStep('pick');
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/tasks/template.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Could not download template'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `tasks_template_${clientId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Network error');
    }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'true');
      const res = await fetch(`${API_BASE}/clients/${clientId}/tasks/bulk-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Could not read CSV');
        setPreviewing(false); return;
      }
      const data = await res.json();
      setPreview(data); setStep('preview');
    } catch { toast.error('Network error'); }
    setPreviewing(false);
  };

  const commitImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'false');
      const res = await fetch(`${API_BASE}/clients/${clientId}/tasks/bulk-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Import failed'));
        setImporting(false); return;
      }
      const data = await res.json();
      toast.success(`${data.imported} tasks imported`);
      onImported?.();
      onOpenChange(false);
      setTimeout(reset, 250);
    } catch { toast.error('Network error'); }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTimeout(reset, 250); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import tasks</DialogTitle>
          <DialogDescription>Upload a CSV of tasks for this client. Use the template if you don't have one yet.</DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2" data-testid="download-template-btn">
              <Download className="w-4 h-4" /> Download CSV template
            </Button>
            <div className="border-2 border-dashed border-[#E6E0D9] rounded-lg p-6 text-center">
              <FileText className="w-8 h-8 text-[#A86A3A] mx-auto mb-3" />
              <input
                type="file" accept=".csv,text/csv" id="bulk-tasks-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="bulk-import-file-input"
              />
              <label htmlFor="bulk-tasks-file" className="cursor-pointer text-sm text-[#A86A3A] hover:underline">
                {file ? <span>Selected: <b>{file.name}</b></span> : 'Click to pick a .csv file'}
              </label>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">Ready to import</p>
                <p className="text-2xl font-light text-emerald-900">{preview.validCount}</p>
              </div>
              <div className={`${preview.errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'} border rounded-md p-3`}>
                <p className="text-[10px] uppercase tracking-wider text-stone-700">Errors</p>
                <p className={`text-2xl font-light ${preview.errorCount > 0 ? 'text-red-700' : 'text-stone-700'}`}>{preview.errorCount}</p>
              </div>
            </div>

            {preview.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                {preview.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-800 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{e.error}</span>
                  </p>
                ))}
              </div>
            )}

            {preview.preview?.length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-[#E6E0D9] rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F7F5] text-[#5D524D]">
                    <tr><th className="p-2 text-left">Title</th><th className="p-2 text-left">Due</th><th className="p-2 text-left">Owner</th><th className="p-2 text-left">Priority</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p, i) => (
                      <tr key={i} className="border-t border-[#E6E0D9]">
                        <td className="p-2">{p.title}</td>
                        <td className="p-2">{p.dueDate || '—'}</td>
                        <td className="p-2">{p.owner || '—'}</td>
                        <td className="p-2">{p.priority}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'pick' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runPreview} disabled={!file || previewing} data-testid="preview-import-btn">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button
                onClick={commitImport}
                disabled={importing || preview?.errorCount > 0 || (preview?.validCount || 0) === 0}
                data-testid="commit-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Import {preview?.validCount || 0} tasks
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============ EXPORT WITH FILTERS ============

export function ExportTasksDialog({ open, onOpenChange, clientId, clientName }) {
  const { token } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    pending: true, inProgress: true, completed: false, cancelled: false,
    includeOneOff: true, includeRecurring: true,
    serviceLine: 'any', dateRange: 'all',
  });

  const buildQueryString = () => {
    const params = new URLSearchParams();
    const statuses = [];
    if (filters.pending) statuses.push('Pending');
    if (filters.inProgress) statuses.push('In Progress');
    if (filters.completed) statuses.push('Completed');
    if (statuses.length && statuses.length < 3) params.set('statuses', statuses.join(','));
    params.set('includeOneOff', String(filters.includeOneOff));
    params.set('includeRecurring', String(filters.includeRecurring));
    if (filters.serviceLine !== 'any') params.set('serviceLine', filters.serviceLine);
    if (filters.dateRange === '12m') {
      const from = new Date(); from.setMonth(from.getMonth() - 12);
      params.set('fromDate', from.toISOString().slice(0, 10));
    } else if (filters.dateRange === '3m') {
      const from = new Date(); from.setMonth(from.getMonth() - 3);
      params.set('fromDate', from.toISOString().slice(0, 10));
    }
    return params.toString();
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const qs = buildQueryString();
      const res = await fetch(`${API_BASE}/clients/${clientId}/tasks/export.csv?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Export failed'); setExporting(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (clientName || 'client').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `tasks_${safe}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      onOpenChange(false);
    } catch { toast.error('Network error'); }
    setExporting(false);
  };

  const Check = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox" checked={!!filters[k]}
        onChange={(e) => setFilters({ ...filters, [k]: e.target.checked })}
        data-testid={`filter-${k}`}
      />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-tasks-dialog">
        <DialogHeader>
          <DialogTitle>Export tasks to CSV</DialogTitle>
          <DialogDescription>Pick what to include. Default is everything not yet completed.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="mb-2 block">Status</Label>
            <div className="space-y-1.5">
              <Check k="pending" label="Pending" />
              <Check k="inProgress" label="In Progress" />
              <Check k="completed" label="Completed" />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Task type</Label>
            <div className="space-y-1.5">
              <Check k="includeOneOff" label="One-off tasks" />
              <Check k="includeRecurring" label="Recurring tasks" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">Date range</Label>
              <Select value={filters.dateRange} onValueChange={(v) => setFilters({ ...filters, dateRange: v })}>
                <SelectTrigger data-testid="filter-date-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                  <SelectItem value="3m">Last 3 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block">Service line</Label>
              <Select value={filters.serviceLine} onValueChange={(v) => setFilters({ ...filters, serviceLine: v })}>
                <SelectTrigger data-testid="filter-service-line"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="family-office">Family Office</SelectItem>
                  <SelectItem value="wealth">Wealth</SelectItem>
                  <SelectItem value="tax">Tax</SelectItem>
                  <SelectItem value="tax-advisory">Tax Advisory</SelectItem>
                  <SelectItem value="philanthropy">Philanthropy</SelectItem>
                  <SelectItem value="trustee-legal">Trustee/Legal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={runExport} disabled={exporting} data-testid="run-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============ ORG-WIDE BULK IMPORT ============
// Same UX as the per-client version, but the CSV needs a leading `clientName`
// column and rows can span multiple clients.

export function OrgBulkImportTasksDialog({ open, onOpenChange, onImported }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('pick');

  const reset = () => { setFile(null); setPreview(null); setStep('pick'); };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/tasks/template.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Could not download template'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `tasks_template_org.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Network error'); }
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'true');
      const res = await fetch(`${API_BASE}/tasks/bulk-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Could not read CSV');
        setPreviewing(false); return;
      }
      const data = await res.json();
      setPreview(data); setStep('preview');
    } catch { toast.error('Network error'); }
    setPreviewing(false);
  };

  const commitImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dryRun', 'false');
      const res = await fetch(`${API_BASE}/tasks/bulk-import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Import failed'));
        setImporting(false); return;
      }
      const data = await res.json();
      toast.success(`${data.imported} tasks imported across ${data.clientsTouched} client${data.clientsTouched === 1 ? '' : 's'}`);
      onImported?.();
      onOpenChange(false);
      setTimeout(reset, 250);
    } catch { toast.error('Network error'); }
    setImporting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTimeout(reset, 250); }}>
      <DialogContent className="max-w-2xl" data-testid="org-bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import tasks (org-wide)</DialogTitle>
          <DialogDescription>Upload one CSV with rows for multiple clients. The first column must be <b>clientName</b> and match an existing client in this organisation.</DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2" data-testid="org-download-template-btn">
              <Download className="w-4 h-4" /> Download org CSV template
            </Button>
            <div className="border-2 border-dashed border-[#E6E0D9] rounded-lg p-6 text-center">
              <FileText className="w-8 h-8 text-[#A86A3A] mx-auto mb-3" />
              <input
                type="file" accept=".csv,text/csv" id="org-bulk-tasks-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="org-bulk-import-file-input"
              />
              <label htmlFor="org-bulk-tasks-file" className="cursor-pointer text-sm text-[#A86A3A] hover:underline">
                {file ? <span>Selected: <b>{file.name}</b></span> : 'Click to pick a .csv file'}
              </label>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700">Ready to import</p>
                <p className="text-2xl font-light text-emerald-900">{preview.validCount}</p>
              </div>
              <div className={`${preview.errorCount > 0 ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'} border rounded-md p-3`}>
                <p className="text-[10px] uppercase tracking-wider text-stone-700">Errors</p>
                <p className={`text-2xl font-light ${preview.errorCount > 0 ? 'text-red-700' : 'text-stone-700'}`}>{preview.errorCount}</p>
              </div>
              <div className="bg-[#F9F7F5] border border-[#E6E0D9] rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#5D524D]">Clients touched</p>
                <p className="text-2xl font-light text-[#2C2420]">{(preview.clientsTouched || []).length}</p>
              </div>
            </div>

            {preview.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
                {preview.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-800 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{e.error}</span>
                  </p>
                ))}
              </div>
            )}

            {preview.preview?.length > 0 && (
              <div className="max-h-48 overflow-y-auto border border-[#E6E0D9] rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F7F5] text-[#5D524D]">
                    <tr><th className="p-2 text-left">Client</th><th className="p-2 text-left">Title</th><th className="p-2 text-left">Due</th><th className="p-2 text-left">Owner</th><th className="p-2 text-left">Priority</th></tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((p, i) => (
                      <tr key={i} className="border-t border-[#E6E0D9]">
                        <td className="p-2 font-medium">{p.clientName}</td>
                        <td className="p-2">{p.title}</td>
                        <td className="p-2">{p.dueDate || '—'}</td>
                        <td className="p-2">{p.owner || '—'}</td>
                        <td className="p-2">{p.priority}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'pick' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runPreview} disabled={!file || previewing} data-testid="org-preview-import-btn">
                {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Preview
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button
                onClick={commitImport}
                disabled={importing || preview?.errorCount > 0 || (preview?.validCount || 0) === 0}
                data-testid="org-commit-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Import {preview?.validCount || 0} tasks
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============ ORG-WIDE EXPORT ============

export function OrgExportTasksDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    pending: true, inProgress: true, completed: false,
    includeOneOff: true, includeRecurring: true,
    serviceLine: 'any', dateRange: 'all',
  });

  const buildQueryString = () => {
    const params = new URLSearchParams();
    const statuses = [];
    if (filters.pending) statuses.push('Pending');
    if (filters.inProgress) statuses.push('In Progress');
    if (filters.completed) statuses.push('Completed');
    if (statuses.length && statuses.length < 3) params.set('statuses', statuses.join(','));
    params.set('includeOneOff', String(filters.includeOneOff));
    params.set('includeRecurring', String(filters.includeRecurring));
    if (filters.serviceLine !== 'any') params.set('serviceLine', filters.serviceLine);
    if (filters.dateRange === '12m') {
      const from = new Date(); from.setMonth(from.getMonth() - 12);
      params.set('fromDate', from.toISOString().slice(0, 10));
    } else if (filters.dateRange === '3m') {
      const from = new Date(); from.setMonth(from.getMonth() - 3);
      params.set('fromDate', from.toISOString().slice(0, 10));
    }
    return params.toString();
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const qs = buildQueryString();
      const res = await fetch(`${API_BASE}/tasks/export.csv?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Export failed'); setExporting(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks_org_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      onOpenChange(false);
    } catch { toast.error('Network error'); }
    setExporting(false);
  };

  const Check = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox" checked={!!filters[k]}
        onChange={(e) => setFilters({ ...filters, [k]: e.target.checked })}
        data-testid={`org-filter-${k}`}
      />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="org-export-tasks-dialog">
        <DialogHeader>
          <DialogTitle>Export tasks to CSV (org-wide)</DialogTitle>
          <DialogDescription>Pick what to include. CSV will include the client name on every row.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="mb-2 block">Status</Label>
            <div className="space-y-1.5">
              <Check k="pending" label="Pending" />
              <Check k="inProgress" label="In Progress" />
              <Check k="completed" label="Completed" />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Task type</Label>
            <div className="space-y-1.5">
              <Check k="includeOneOff" label="One-off tasks" />
              <Check k="includeRecurring" label="Recurring tasks" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">Date range</Label>
              <Select value={filters.dateRange} onValueChange={(v) => setFilters({ ...filters, dateRange: v })}>
                <SelectTrigger data-testid="org-filter-date-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                  <SelectItem value="3m">Last 3 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block">Service line</Label>
              <Select value={filters.serviceLine} onValueChange={(v) => setFilters({ ...filters, serviceLine: v })}>
                <SelectTrigger data-testid="org-filter-service-line"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="family-office">Family Office</SelectItem>
                  <SelectItem value="wealth">Wealth</SelectItem>
                  <SelectItem value="tax">Tax</SelectItem>
                  <SelectItem value="tax-advisory">Tax Advisory</SelectItem>
                  <SelectItem value="philanthropy">Philanthropy</SelectItem>
                  <SelectItem value="trustee-legal">Trustee/Legal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={runExport} disabled={exporting} data-testid="org-run-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
